import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import ImageLightbox from '../components/blocks/ImageLightbox';
import HiringApplicationModal from '../components/blocks/HiringApplicationModal';
import DeletePostConfirmModal from '../components/blocks/DeletePostConfirmModal';
import { useAuth, isFounder } from '../contexts/AuthContext';
import { handleMarkdownShortcut } from '../utils/textareaMarkdownShortcuts';
import InlineMarkdown, { stripInlineMarkdown } from '../components/blocks/InlineMarkdown';
import PostMarkdownImport from '../components/blocks/PostMarkdownImport';
import {
  alignHiringProgrammeNames,
  hiringProgrammeNamesNeedAlign,
  persistAlignedHiringPost
} from '../utils/alignHiringProgrammeNames';

const Hiring = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0, alt: '' });
  const { currentUser } = useAuth();
  const canAdmin = isFounder(currentUser);

  const [applyModal, setApplyModal] = useState({ open: false, slug: '', title: '' });
  const [deleteModal, setDeleteModal] = useState({ open: false, slug: '', title: '', mode: 'archive' });
  const [restoringSlug, setRestoringSlug] = useState('');

  const [adminOpen, setAdminOpen] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [adminForm, setAdminForm] = useState({
    slug: '',
    publishedAt: '',
    linkedinUrl: '',
    title_en: '',
    title_tc: '',
    title_sc: '',
    summary_en: '',
    summary_tc: '',
    summary_sc: '',
    body_en: '',
    body_tc: '',
    body_sc: ''
  });
  const [adminImages, setAdminImages] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [editOriginalSlug, setEditOriginalSlug] = useState('');

  const lang = useMemo(() => {
    const raw = (i18n.language || 'en').toLowerCase();
    if (raw.startsWith('tc')) return 'tc';
    if (raw.startsWith('sc')) return 'sc';
    return 'en';
  }, [i18n.language]);

  const pickLocalized = (post, fieldBase) => {
    const key = `${fieldBase}_${lang}`;
    return post?.[key] || post?.[fieldBase] || '';
  };

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const url =
          canAdmin && token ? '/api/hiring?includeArchived=true' : '/api/hiring';
        const response = await fetch(url, {
          ...(canAdmin && token ? { headers: { Authorization: `Bearer ${token}` } } : {})
        });
        if (!response.ok) {
          throw new Error(`Failed to load (${response.status})`);
        }
        const data = await response.json();
        const originals = (data || []).filter((post) => !post.deleted);
        setPosts(originals.map(alignHiringProgrammeNames));
        setError(null);
        if (canAdmin && token) {
          void Promise.all(
            originals
              .filter(hiringProgrammeNamesNeedAlign)
              .map((post) => persistAlignedHiringPost(post.slug, token))
          );
        }
      } catch (err) {
        console.error('Error loading hiring posts:', err);
        setError(err.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [canAdmin]);

  const resetAdminFields = () => {
    setEditOriginalSlug('');
    setAdminForm({
      slug: '',
      publishedAt: '',
      linkedinUrl: '',
      title_en: '',
      title_tc: '',
      title_sc: '',
      summary_en: '',
      summary_tc: '',
      summary_sc: '',
      body_en: '',
      body_tc: '',
      body_sc: ''
    });
    setAdminImages([]);
    setHeroIndex(0);
    setSelectedFiles([]);
  };

  const fetchAndOpenEdit = useCallback(
    async (slug) => {
      try {
        setAdminError(null);
        const token = localStorage.getItem('token');
        const url =
          canAdmin && token
            ? `/api/hiring/${encodeURIComponent(slug)}?includeArchived=true`
            : `/api/hiring/${encodeURIComponent(slug)}`;
        const response = await fetch(url, {
          ...(canAdmin && token ? { headers: { Authorization: `Bearer ${token}` } } : {})
        });
        if (!response.ok) {
          throw new Error(t('hiring.admin.errors.loadFailed'));
        }
        const r = alignHiringProgrammeNames(await response.json());
        setEditOriginalSlug(r.slug);
        setAdminForm({
          slug: r.slug,
          publishedAt: r.publishedAt ? new Date(r.publishedAt).toISOString().slice(0, 10) : '',
          linkedinUrl: r.linkedinUrl || '',
          title_en: r.title_en || '',
          title_tc: r.title_tc || '',
          title_sc: r.title_sc || '',
          summary_en: r.summary_en || '',
          summary_tc: r.summary_tc || '',
          summary_sc: r.summary_sc || '',
          body_en: r.body_en || '',
          body_tc: r.body_tc || '',
          body_sc: r.body_sc || ''
        });
        const imgs = Array.isArray(r.images) ? [...r.images] : [];
        setAdminImages(imgs);
        const hero = r.heroImageUrl || '';
        const hi = hero ? imgs.indexOf(hero) : 0;
        setHeroIndex(hi >= 0 ? hi : 0);
        setSelectedFiles([]);
        setAdminOpen(true);
      } catch (err) {
        console.error(err);
        setAdminError(err.message || t('hiring.admin.errors.loadFailed'));
      }
    },
    [t, canAdmin]
  );

  useEffect(() => {
    const openEditSlug = location.state?.openEditSlug;
    if (!openEditSlug || !canAdmin) return;
    let cancelled = false;
    (async () => {
      await fetchAndOpenEdit(openEditSlug);
      if (!cancelled) {
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.state?.openEditSlug, canAdmin, fetchAndOpenEdit, navigate, location.pathname]);

  const formatDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleRestorePost = async (slug) => {
    if (!slug || restoringSlug) return;
    try {
      setRestoringSlug(slug);
      setError(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/hiring/${encodeURIComponent(slug)}/restore`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || t('hiring.restoreError'));
      }
      await refreshPosts();
    } catch (err) {
      console.error('Restore error:', err);
      setError(err.message || t('hiring.restoreError'));
    } finally {
      setRestoringSlug('');
    }
  };

  const refreshPosts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url =
        canAdmin && token ? '/api/hiring?includeArchived=true' : '/api/hiring';
      const response = await fetch(url, {
        ...(canAdmin && token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      });
      if (!response.ok) {
        throw new Error(`Failed to load (${response.status})`);
      }
      const data = await response.json();
      setPosts((data || []).filter((post) => !post.deleted).map(alignHiringProgrammeNames));
      setError(null);
    } catch (err) {
      console.error('Error refreshing hiring posts:', err);
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const openCreatePanel = () => {
    setLightbox({ open: false, images: [], index: 0, alt: '' });
    setAdminError(null);
    resetAdminFields();
    setAdminOpen(true);
  };

  const closeAdminPanel = () => {
    setAdminOpen(false);
    setAdminError(null);
    resetAdminFields();
  };

  const handleRemoveAdminImage = (removeIndex) => {
    setAdminImages((prev) => prev.filter((_, i) => i !== removeIndex));
    setHeroIndex((prevHeroIndex) => {
      if (prevHeroIndex === removeIndex) return 0;
      if (prevHeroIndex > removeIndex) return prevHeroIndex - 1;
      return prevHeroIndex;
    });
  };

  const uploadSelectedImages = async (files, uploadSlug) => {
    if (!uploadSlug) {
      throw new Error(t('hiring.admin.errors.slugBeforeUpload'));
    }
    if (!files.length) {
      throw new Error(t('hiring.admin.errors.selectPhotosFirst'));
    }

    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('slug', uploadSlug);
    files.forEach((file) => formData.append('images', file));

    const res = await fetch('/api/hiring/upload-images', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    });

    const data = await res.json().catch(() => null);
    if (!res.ok) {
      throw new Error(data?.message || `Upload failed (${res.status})`);
    }

    const urls = data?.urls || [];
    if (!urls.length) {
      throw new Error(t('hiring.admin.errors.uploadNoUrls'));
    }
    return urls;
  };

  const handleUploadImages = async () => {
    try {
      setUploading(true);
      setAdminError(null);

      const uploadSlug = (editOriginalSlug || adminForm.slug).trim();
      const urls = await uploadSelectedImages(selectedFiles, uploadSlug);

      setAdminImages((prev) => [...prev, ...urls]);
      setHeroIndex((prev) => (prev > 0 || urls.length === 0 ? prev : 0));
      setSelectedFiles([]);
    } catch (err) {
      console.error('Upload error:', err);
      setAdminError(err.message || 'Failed to upload photos');
    } finally {
      setUploading(false);
    }
  };

  const handleSavePost = async () => {
    try {
      setAdminError(null);

      const slug = adminForm.slug.trim();
      if (!slug) throw new Error(t('hiring.admin.errors.slugRequired'));

      const titleBase =
        (adminForm.title_en || adminForm.title_tc || adminForm.title_sc || '').trim();
      if (!titleBase) throw new Error(t('hiring.admin.errors.titleRequiredAny'));

      const summaryBase =
        (adminForm.summary_en || adminForm.summary_tc || adminForm.summary_sc || '').trim();
      const bodyBase =
        (adminForm.body_en || adminForm.body_tc || adminForm.body_sc || '').trim();

      const token = localStorage.getItem('token');
      let images = [...adminImages];

      if (selectedFiles.length > 0) {
        setUploading(true);
        try {
          const uploadSlug = (editOriginalSlug || slug).trim();
          const urls = await uploadSelectedImages(selectedFiles, uploadSlug);
          images = [...images, ...urls];
          setAdminImages(images);
          setSelectedFiles([]);
        } finally {
          setUploading(false);
        }
      }

      const heroImageUrl = images[heroIndex] || images[0] || '';

      const payload = {
        slug,
        title: titleBase,
        title_en: adminForm.title_en || '',
        title_tc: adminForm.title_tc || '',
        title_sc: adminForm.title_sc || '',
        summary: summaryBase,
        summary_en: adminForm.summary_en || '',
        summary_tc: adminForm.summary_tc || '',
        summary_sc: adminForm.summary_sc || '',
        body: bodyBase,
        body_en: adminForm.body_en || '',
        body_tc: adminForm.body_tc || '',
        body_sc: adminForm.body_sc || '',
        linkedinUrl: adminForm.linkedinUrl || '',
        heroImageUrl,
        heroImageAlt: titleBase,
        images,
        isPublished: true
      };

      if (adminForm.publishedAt) payload.publishedAt = adminForm.publishedAt;

      if (editOriginalSlug) {
        payload.previousSlug = editOriginalSlug.trim();
      }

      const res = await fetch('/api/hiring', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || `Save failed (${res.status})`);
      }

      closeAdminPanel();

      await refreshPosts();
    } catch (err) {
      console.error('Save error:', err);
      setAdminError(err.message || 'Failed to save');
      setUploading(false);
    }
  };

  return (
    <div className="bg-canvas min-h-screen">
      <DeletePostConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, slug: '', title: '', mode: 'archive' })}
        variant="hiring"
        mode={deleteModal.mode}
        slug={deleteModal.slug}
        itemTitle={deleteModal.title}
        onSuccess={refreshPosts}
      />
      <HiringApplicationModal
        isOpen={applyModal.open}
        onClose={() => setApplyModal({ open: false, slug: '', title: '' })}
        jobSlug={applyModal.slug}
        jobTitle={applyModal.title}
      />
      <ImageLightbox
        isOpen={lightbox.open}
        images={lightbox.images}
        index={lightbox.index}
        alt={lightbox.alt}
        onClose={() => setLightbox({ open: false, images: [], index: 0, alt: '' })}
        onPrev={() =>
          setLightbox((prev) => ({
            ...prev,
            index: (prev.index - 1 + (prev.images?.length || 1)) % (prev.images?.length || 1)
          }))
        }
        onNext={() =>
          setLightbox((prev) => ({
            ...prev,
            index: (prev.index + 1) % (prev.images?.length || 1)
          }))
        }
      />

      {canAdmin && (
        <>
          <div className="fixed bottom-6 right-6 z-[220] group">
            <button
              type="button"
              onClick={openCreatePanel}
              className="relative inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary hover:bg-primary-hover text-primary-foreground shadow-lg transition-transform duration-200 ease-out hover:scale-110 active:scale-95"
              aria-label={t('hiring.addPost')}
            >
              <span
                className="absolute inset-0 rounded-full bg-primary/40 animate-[ping_3s_cubic-bezier(0,0,0.2,1)_infinite]"
                aria-hidden
              />
              <span className="relative font-extrabold text-3xl leading-none">+</span>
            </button>
            <div className="pointer-events-none absolute right-full mr-3 bottom-1/2 translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
              <div className="bg-zinc-900 text-white text-xs font-semibold px-3 py-1.5 rounded-md shadow-lg whitespace-nowrap">
                {t('hiring.addPost')}
              </div>
            </div>
          </div>

          {adminOpen && (
            <div
              className="fixed inset-0 z-[230] bg-black/30 flex items-end justify-end p-4"
              onClick={closeAdminPanel}
              role="dialog"
              aria-modal="true"
            >
              <div
                className="w-full max-w-2xl bg-surface rounded-xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-4 border-b">
                  <h3 className="text-base font-semibold text-ink">
                    {editOriginalSlug ? t('hiring.admin.panelTitleEdit') : t('hiring.admin.panelTitle')}
                  </h3>
                  <button
                    type="button"
                    onClick={closeAdminPanel}
                    className="text-ink-subtle hover:text-ink text-xl leading-none"
                    aria-label={t('hiring.admin.closePanel')}
                  >
                    ×
                  </button>
                </div>

                <div className="p-4 max-h-[80vh] overflow-y-auto space-y-4">
                  {adminError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{adminError}</div>
                  )}

                  <PostMarkdownImport variant="hiring" onApply={setAdminForm} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-ink-muted">{t('hiring.admin.slug')}</p>
                      <input
                        value={adminForm.slug}
                        onChange={(e) => setAdminForm((p) => ({ ...p, slug: e.target.value }))}
                        className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        placeholder={t('hiring.admin.slugPlaceholder')}
                      />
                      {editOriginalSlug ? (
                        <p className="text-xs text-ink-subtle mt-1">{t('hiring.admin.slugChangeHint')}</p>
                      ) : null}
                    </div>

                    <div className="space-y-1">
                      <p className="text-sm font-medium text-ink-muted">{t('hiring.admin.publishedAt')}</p>
                      <input
                        type="date"
                        value={adminForm.publishedAt}
                        onChange={(e) => setAdminForm((p) => ({ ...p, publishedAt: e.target.value }))}
                        className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm font-medium text-ink-muted">{t('hiring.admin.linkedInUrl')}</p>
                    <input
                      value={adminForm.linkedinUrl}
                      onChange={(e) => setAdminForm((p) => ({ ...p, linkedinUrl: e.target.value }))}
                      className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                      placeholder={t('hiring.admin.optional')}
                    />
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-ink">{t('hiring.admin.titles')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <p className="text-xs text-ink-muted">{t('hiring.admin.langEn')}</p>
                        <input
                          value={adminForm.title_en}
                          onChange={(e) => setAdminForm((p) => ({ ...p, title_en: e.target.value }))}
                          onKeyDown={(e) =>
                            handleMarkdownShortcut(e, adminForm.title_en, (next) =>
                              setAdminForm((p) => ({ ...p, title_en: next }))
                            )
                          }
                          className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-ink-muted">{t('hiring.admin.langTc')}</p>
                        <input
                          value={adminForm.title_tc}
                          onChange={(e) => setAdminForm((p) => ({ ...p, title_tc: e.target.value }))}
                          onKeyDown={(e) =>
                            handleMarkdownShortcut(e, adminForm.title_tc, (next) =>
                              setAdminForm((p) => ({ ...p, title_tc: next }))
                            )
                          }
                          className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-ink-muted">{t('hiring.admin.langSc')}</p>
                        <input
                          value={adminForm.title_sc}
                          onChange={(e) => setAdminForm((p) => ({ ...p, title_sc: e.target.value }))}
                          onKeyDown={(e) =>
                            handleMarkdownShortcut(e, adminForm.title_sc, (next) =>
                              setAdminForm((p) => ({ ...p, title_sc: next }))
                            )
                          }
                          className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-ink">{t('hiring.admin.summaries')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <p className="text-xs text-ink-muted">{t('hiring.admin.langEn')}</p>
                        <textarea
                          value={adminForm.summary_en}
                          onChange={(e) => setAdminForm((p) => ({ ...p, summary_en: e.target.value }))}
                          onKeyDown={(e) =>
                            handleMarkdownShortcut(e, adminForm.summary_en, (next) =>
                              setAdminForm((p) => ({ ...p, summary_en: next }))
                            )
                          }
                          rows={3}
                          className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-ink-muted">{t('hiring.admin.langTc')}</p>
                        <textarea
                          value={adminForm.summary_tc}
                          onChange={(e) => setAdminForm((p) => ({ ...p, summary_tc: e.target.value }))}
                          onKeyDown={(e) =>
                            handleMarkdownShortcut(e, adminForm.summary_tc, (next) =>
                              setAdminForm((p) => ({ ...p, summary_tc: next }))
                            )
                          }
                          rows={3}
                          className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-ink-muted">{t('hiring.admin.langSc')}</p>
                        <textarea
                          value={adminForm.summary_sc}
                          onChange={(e) => setAdminForm((p) => ({ ...p, summary_sc: e.target.value }))}
                          onKeyDown={(e) =>
                            handleMarkdownShortcut(e, adminForm.summary_sc, (next) =>
                              setAdminForm((p) => ({ ...p, summary_sc: next }))
                            )
                          }
                          rows={3}
                          className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-ink">{t('hiring.admin.bodies')}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <p className="text-xs text-ink-muted">{t('hiring.admin.langEn')}</p>
                        <textarea
                          value={adminForm.body_en}
                          onChange={(e) => setAdminForm((p) => ({ ...p, body_en: e.target.value }))}
                          onKeyDown={(e) =>
                            handleMarkdownShortcut(e, adminForm.body_en, (next) =>
                              setAdminForm((p) => ({ ...p, body_en: next }))
                            )
                          }
                          rows={6}
                          className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-ink-muted">{t('hiring.admin.langTc')}</p>
                        <textarea
                          value={adminForm.body_tc}
                          onChange={(e) => setAdminForm((p) => ({ ...p, body_tc: e.target.value }))}
                          onKeyDown={(e) =>
                            handleMarkdownShortcut(e, adminForm.body_tc, (next) =>
                              setAdminForm((p) => ({ ...p, body_tc: next }))
                            )
                          }
                          rows={6}
                          className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-ink-muted">{t('hiring.admin.langSc')}</p>
                        <textarea
                          value={adminForm.body_sc}
                          onChange={(e) => setAdminForm((p) => ({ ...p, body_sc: e.target.value }))}
                          onKeyDown={(e) =>
                            handleMarkdownShortcut(e, adminForm.body_sc, (next) =>
                              setAdminForm((p) => ({ ...p, body_sc: next }))
                            )
                          }
                          rows={6}
                          className="w-full border border-edge-strong rounded-lg p-2 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-ink">{t('hiring.admin.photos')}</p>
                    {adminImages.length ? (
                      <div className="grid grid-cols-3 gap-2">
                        {adminImages.map((url, i) => (
                          <div
                            key={url + i}
                            className={`relative rounded-lg overflow-hidden border ${
                              i === heroIndex ? 'border-primary' : 'border-edge'
                            }`}
                          >
                            <img
                              src={url}
                              alt=""
                              className="w-full h-24 object-cover cursor-pointer"
                              onClick={() => setHeroIndex(i)}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveAdminImage(i)}
                              className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs hover:bg-black/80 flex items-center justify-center"
                              aria-label={t('hiring.admin.removeImage')}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-ink-subtle">{t('hiring.admin.noPhotos')}</p>
                    )}

                    <div className="space-y-2">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setSelectedFiles(files);
                        }}
                        className="text-sm"
                      />
                      <button
                        type="button"
                        onClick={handleUploadImages}
                        disabled={uploading || !selectedFiles.length}
                        className="inline-flex items-center justify-center px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {uploading ? t('hiring.admin.uploading') : t('hiring.admin.uploadPhotos')}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="p-4 border-t flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeAdminPanel}
                    className="px-4 py-2 rounded-lg border border-edge-strong text-ink-muted hover:bg-surface-muted text-sm"
                  >
                    {t('hiring.admin.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSavePost}
                    disabled={uploading}
                    className="px-4 py-2 rounded-lg bg-primary hover:bg-primary-hover text-primary-foreground text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('hiring.admin.save')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-ink">{t('hiring.title')}</h1>
          <p className="mt-2 text-ink-muted">{t('hiring.subtitle')}</p>
        </div>

        {loading && (
          <div className="bg-surface rounded-lg shadow-sm p-6 text-center">
            <p className="text-ink-muted">{t('hiring.loading')}</p>
          </div>
        )}

        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">{t('hiring.error')}</p>
            <p className="text-red-500 text-xs mt-1">{error}</p>
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="bg-surface rounded-lg shadow-sm p-6 text-center">
            <p className="text-ink-muted">{t('hiring.empty')}</p>
          </div>
        )}

        <div className="space-y-6">
          {posts.map((post) => (
            <article
              key={post.slug}
              className={`bg-surface rounded-lg shadow-sm overflow-hidden border transition-transform transition-shadow duration-150 ease-out hover:shadow-md hover:-translate-y-0.5 cursor-pointer ${
                post.archived ? 'border-amber-200' : 'border-edge'
              }`}
              role="button"
              onClick={() => navigate(`/hiring/${post.slug}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/hiring/${post.slug}`);
                }
              }}
              tabIndex={0}
            >
              {(() => {
                const title = pickLocalized(post, 'title');
                const summary = pickLocalized(post, 'summary');
                const altText = stripInlineMarkdown(post.heroImageAlt || title);
                const images =
                  Array.isArray(post.images) && post.images.length > 0
                    ? post.images
                    : post.heroImageUrl
                      ? [post.heroImageUrl]
                      : [];

                return (
                  <>
                    {images.length > 0 && (
                      <div className="w-full bg-surface-muted overflow-hidden">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-[1px]">
                          {images.map((src, index) => (
                            <div
                              key={src || index}
                              className="h-40 md:h-48 bg-surface-muted overflow-hidden group"
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setLightbox({ open: true, images, index, alt: altText });
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setLightbox({ open: true, images, index, alt: altText });
                                }
                              }}
                            >
                              <img
                                src={src}
                                alt={altText}
                                className="w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-[1.03]"
                                loading="lazy"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <div className="flex items-center gap-2 flex-wrap min-w-0">
                          <h2 className="text-xl font-semibold text-ink">
                            <InlineMarkdown variant="heading">{title}</InlineMarkdown>
                          </h2>
                          {post.archived && (
                            <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200 shrink-0 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-800/60">
                              {t('hiring.archivedBadge')}
                            </span>
                          )}
                        </div>
                        {post.publishedAt && (
                          <time dateTime={post.publishedAt} className="text-sm text-ink-subtle whitespace-nowrap">
                            {formatDate(post.publishedAt)}
                          </time>
                        )}
                      </div>
                      {summary && (
                        <div className="text-ink-muted mb-4">
                          <InlineMarkdown variant="body">{summary}</InlineMarkdown>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        {post.linkedinUrl && (
                          <a
                            href={post.linkedinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary-hover"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {t('hiring.viewOnLinkedIn')}
                          </a>
                        )}
                        {!post.archived && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setApplyModal({ open: true, slug: post.slug, title });
                            }}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-primary text-link bg-surface hover:bg-accent/25"
                          >
                            {t('hiring.applyByEmail')}
                          </button>
                        )}
                        {canAdmin && (
                          <>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchAndOpenEdit(post.slug);
                              }}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-edge-strong text-ink bg-surface hover:bg-surface-muted"
                            >
                              {t('hiring.admin.editPost')}
                            </button>
                            {post.archived ? (
                              <>
                              <button
                                type="button"
                                disabled={restoringSlug === post.slug}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRestorePost(post.slug);
                                }}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-emerald-500 text-emerald-800 bg-surface hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-emerald-500 dark:text-emerald-200 dark:hover:bg-emerald-950/40"
                              >
                                {restoringSlug === post.slug ? t('hiring.restoring') : t('hiring.restorePost')}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteModal({ open: true, slug: post.slug, title, mode: 'remove' });
                                }}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-danger text-danger bg-surface hover:bg-danger/10"
                              >
                                {t('deleteConfirm.removeButton')}
                              </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteModal({ open: true, slug: post.slug, title, mode: 'archive' });
                                }}
                                className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-amber-400 text-amber-800 bg-surface hover:bg-amber-50 dark:border-amber-500 dark:text-amber-200 dark:hover:bg-amber-950/40"
                              >
                                {t('deleteConfirm.deleteButton')}
                              </button>
                            )}
                          </>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/hiring/${post.slug}`);
                          }}
                          className="text-sm text-ink-subtle hover:text-ink ml-auto"
                        >
                          {t('hiring.clickToExpand')}
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Hiring;
